import { useState, useMemo } from 'react';
import { Car, Bike, Users, CheckCircle2, ArrowRight } from 'lucide-react';

export default function DriversPage() {
  const [formData, setFormData] = useState({
    name: '',
    age: '',
    phone: '',
    vehicle_type: 'bike' as 'bike' | 'moped' | 'car',
  });
  const [submitted, setSubmitted] = useState(false);

  const mailtoBase = useMemo(() => {
    const subject = encodeURIComponent('Søknad: Sjåfør hos Gjesdalivery');
    return `mailto:kontakt@gjesdalivery.no?subject=${subject}`;
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.age || !formData.phone) {
      alert('Vennligst fyll inn alle feltene');
      return;
    }

    const body = encodeURIComponent(
      [
        `Navn: ${formData.name}`,
        `Alder: ${formData.age}`,
        `Telefon: ${formData.phone}`,
        `Kjøretøy: ${labelForVehicle(formData.vehicle_type)}`,
        '',
        'Hei! Jeg ønsker å bli sjåfør hos Gjesdalivery.',
        'Jeg er tilgjengelig på telefon for en kort prat.',
      ].join('\n')
    );

    window.location.href = `${mailtoBase}&body=${body}`;
    setSubmitted(true);
    setFormData({ name: '', age: '', phone: '', vehicle_type: 'bike' });
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-[#0f1724] dark:text-white transition-colors">
      {/* HERO – samme card-vibe som kokksiden */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="relative rounded-[2rem] border border-white/10 bg-white/85 dark:bg-[#121b2e]/95 backdrop-blur-md shadow-[0_10px_40px_rgba(0,0,0,.12)] dark:shadow-[0_10px_40px_rgba(0,0,0,.35)] p-10 md:p-14 text-center">
            <div className="h-16 w-16 mx-auto mb-6 inline-flex items-center justify-center rounded-2xl bg-white/70 dark:bg-black/30 shadow">
              <Bike className="w-8 h-8 text-[#004D99] dark:text-orange-400" />
            </div>

            <h1 className="text-4xl md:text-6xl font-bold tracking-tight">
              Bli sjåfør hos Gjesdalivery
            </h1>
            <p className="mt-4 text-lg md:text-xl text-gray-700 dark:text-white/75 max-w-2xl mx-auto">
              Jobb når det passer deg. Tjen godt. Lever mat for lokale restauranter og butikker i Gjesdal.
            </p>

            <a
              href="#apply"
              className="mt-8 inline-flex items-center justify-center px-8 py-4 rounded-xl bg-[#004D99] hover:bg-[#003366] text-white font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-[1.03]"
            >
              Søk nå
              <ArrowRight className="ml-2 h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* HVORFOR – rene oransje ikoner, ingen kort */}
      <section className="py-14">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-semibold text-center mb-12">
            Hvorfor kjøre for oss?
          </h2>
          <div className="grid md:grid-cols-3 gap-12 text-center">
            <PlainFeature
              Icon={Users}
              title="Fleksibel tid"
              desc="Sett dine egne tider. Ingen faste skift."
            />
            <PlainFeature
              Icon={Car}
              title="Egen transport"
              desc="Sykkel, moped eller bil – du bestemmer."
            />
            <PlainFeature
              Icon={Bike}
              title="God lønn"
              desc="Konkurransedyktig betalt + tips."
            />
          </div>
        </div>
      </section>

      {/* SØKNAD / SUKSESS – ryddig card */}
      <section id="apply" className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          {submitted ? (
            <div className="rounded-2xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-10 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-600 dark:text-green-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Takk for søknaden!</h2>
              <p className="text-gray-700 dark:text-white/75">
                E-postklienten din åpnet med ferdig utfylt søknad. Send den derfra. Vi tar kontakt snart.
              </p>
              <a href={mailtoBase} className="inline-block mt-4 text-sm text-[#004D99] hover:underline">
                Åpne e-posten på nytt
              </a>
            </div>
          ) : (
            <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-white/85 dark:bg-[#121b2e]/95 p-8 shadow-lg">
              <h2 className="text-2xl font-bold mb-6">Søk om å bli sjåfør</h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <Field
                  label="Fullt navn"
                  type="text"
                  value={formData.name}
                  onChange={(v) => setFormData({ ...formData, name: v })}
                  placeholder="Ola Nordmann"
                />

                <Field
                  label="Alder"
                  type="number"
                  min={15}
                  max={99}
                  value={formData.age}
                  onChange={(v) => setFormData({ ...formData, age: v })}
                  placeholder="18"
                />

                <Field
                  label="Telefonnummer"
                  type="tel"
                  value={formData.phone}
                  onChange={(v) => setFormData({ ...formData, phone: v })}
                  placeholder="+47 123 45 678"
                />

                {/* kjøretøyvalg */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Type kjøretøy
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { value: 'bike', label: 'Sykkel', icon: Bike },
                      { value: 'moped', label: 'Moped', icon: Bike }, // bytt til eget moped-ikon hvis du vil
                      { value: 'car', label: 'Bil', icon: Car },
                    ].map(({ value, label, icon: Icon }) => {
                      const active = formData.vehicle_type === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setFormData({ ...formData, vehicle_type: value as any })}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            active
                              ? 'border-[#004D99] bg-blue-50 dark:bg-blue-900/20'
                              : 'border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600'
                          }`}
                        >
                          <Icon
                            className={`w-8 h-8 mx-auto mb-2 ${
                              active ? 'text-[#004D99] dark:text-orange-400' : 'text-gray-600 dark:text-gray-400'
                            }`}
                          />
                          <span
                            className={`text-sm font-medium ${
                              active ? 'text-[#004D99] dark:text-orange-400' : 'text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-xl bg-[#004D99] hover:bg-[#003366] text-white font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                  Send søknad
                </button>
                <p className="text-center text-sm text-gray-500 dark:text-white/60">
                  Vi ringer deg for en kort prat etter at vi mottar e-posten din.
                </p>
              </form>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

/* ——— små byggeklosser ——— */

function PlainFeature({
  Icon,
  title,
  desc,
}: {
  Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  title: string;
  desc: string;
}) {
  return (
    <div className="flex flex-col items-center">
      <Icon className="h-12 w-12 text-orange-400 mb-5" strokeWidth={2.2} />
      <h3 className="text-2xl font-semibold mb-2">{title}</h3>
      <p className="text-base leading-relaxed text-gray-700 dark:text-white/70 max-w-md">{desc}</p>
    </div>
  );
}

function Field({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  min,
  max,
}: {
  label: string;
  type?: string;
  value: string | number;
  onChange: (v: any) => void;
  placeholder?: string;
  min?: number;
  max?: number;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>
      <input
        type={type}
        value={value as any}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-600 text-gray-900 dark:text-white"
        placeholder={placeholder}
        required
      />
    </div>
  );
}

function labelForVehicle(v: 'bike' | 'moped' | 'car') {
  if (v === 'bike') return 'Sykkel';
  if (v === 'moped') return 'Moped';
  return 'Bil';
}
